<template>
  <div
    class="relative !-mx-6 w-[calc(100%+3rem)] overflow-hidden !px-0 !py-6 md:!-mx-8 md:w-[calc(100%+4rem)]"
    :class="[
      {
        'after:pointer-events-none after:absolute after:left-0 after:top-0 after:z-[2] after:hidden after:h-full after:w-full after:bg-gradient-to-r after:via-[percentage:3rem_calc(100%-3rem)] after:content-[\'\'] md:!-mx-8 md:!px-0 md:after:block':
          !noSideGradients,
      },
      {
        'after:from-grey-50 after:via-grey-50/0 after:to-grey-50':
          !noSideGradients && onLight,
      },
      {
        'after:from-grey-100 after:via-grey-100/0 after:to-grey-100':
          !noSideGradients && !onLight,
      },
    ]"
  >
    <!-- Slider controls -->
    <button
      ref="button-previous"
      type="button"
      class="group absolute left-0 top-0 z-10 hidden h-full cursor-pointer items-center justify-center focus:outline-none lg:flex"
      :class="{ 'sm:flex': controlsTablet }"
    >
      <span
        class="inline-flex size-12 items-center justify-center rounded-full opacity-65 group-hover:opacity-100"
        :class="onDark ? 'bg-jag-950' : 'bg-green-800'"
      >
        <IconChevronLeft aria-hidden="true" class="size-7 fill-white" />
        <span class="sr-only">Previous slide</span>
      </span>
    </button>
    <button
      ref="button-next"
      type="button"
      class="group absolute right-0 top-0 z-10 hidden h-full cursor-pointer items-center justify-center focus:outline-none lg:flex"
      :class="{ 'sm:flex': controlsTablet }"
    >
      <span
        class="inline-flex h-12 w-12 items-center justify-center rounded-full opacity-60 group-hover:opacity-100"
        :class="onDark ? 'bg-jag-950' : 'bg-green-800'"
      >
        <IconChevronRight aria-hidden="true" class="size-7 fill-white" />
        <span class="sr-only">Next slide</span>
      </span>
    </button>
    <!-- /Slider controls -->

    <ClientOnly>
      <swiper-container
        ref="swiperRef"
        :initial-slide="setInitialSlide()"
        :grab-cursor="true"
        :speed="400"
        :threshold="12"
        :centered-slides="true"
        :slide-to-clicked-slide="true"
        :slides-per-view="1.1"
        :space-between="16"
        :breakpoints="{
          640: {
            centeredSlides: false,
            slideToClickedSlide: false,
            slidesPerView: 2,
            spaceBetween: 8,
          },
          1024: {
            centeredSlides: false,
            slideToClickedSlide: false,
            slidesPerView: 3,
            spaceBetween: 12,
          },
          1124: {
            centeredSlides: false,
            slideToClickedSlide: false,
            slidesPerView: props.desktopFourCols ? 4 : 3,
            spaceBetween: props.desktopFourCols ? 24 : 16,
            padding: '0 1rem',
          },
        }"
        :navigation="{
          nextEl: buttonNext,
          prevEl: buttonPrevious,
        }"
        :pagination="{
          clickable: true,
        }"
        class="mx-auto w-full !overflow-visible xs:!px-4 sm:!px-8 md:!px-8"
        :class="{ 'swiper-on-dark': onDark }"
      >
        <slot></slot>
      </swiper-container>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
interface Props {
  initialSlide?: number;
  controlsTablet?: boolean;
  desktopFourCols?: boolean;
  noSideGradients?: boolean;
  onLight?: boolean;
  onDark?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  initialSlide: 0,
});

const swiperRef = ref(null);
const buttonNext = useTemplateRef<HTMLElement>("button-next");
const buttonPrevious = useTemplateRef<HTMLElement>("button-previous");
const swiper = useSwiper(swiperRef);

const activeIndex = ref(props.initialSlide);

watch(swiper.instance, (newVal) => {
  newVal?.on("slideChange", () => (activeIndex.value = newVal.activeIndex));
  newVal?.on("resize", () => (activeIndex.value = newVal.activeIndex));
});

defineExpose({ swiper, activeIndex });

const setInitialSlide = () => {
  const initialSlide = props.initialSlide < 0 ? 0 : props.initialSlide;
  return initialSlide > 0 && import.meta.client && window.innerWidth >= 768
    ? initialSlide - 1
    : initialSlide;
};
</script>
<style scoped>
.swiper-button-disabled {
  display: none !important;
}

swiper-container::part(container) {
  overflow: visible;
}
</style>
